import type { ReactNode } from 'react';

import { Platform } from 'react-native';

import { XStack, YStack } from 'tamagui';

import { BodyS, LabelM, getTestProps } from '@south-street-app/atoms';

const pressStyle = { opacity: 0.8 };

const formatConversationTimestamp = (date: Date | null | undefined) => {
  if (!date) {
    return '';
  }

  const now = new Date();
  const isSameDay = now.toDateString() === date.toDateString();

  if (isSameDay) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  if (now.getFullYear() === date.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
};

type ConversationRowProps = {
  avatar: ReactNode;
  testID?: string;
  title: string;
  subtitle: string;
  timestamp?: Date | null;
  timestampLabel?: string;
  unreadCount?: number;
  unreadBadgeTestID?: string;
  selected?: boolean;
  selectedBackgroundColor?: '$mono200' | '$brand100';
  onPress: () => void;
};

const ConversationRow = ({
  avatar,
  testID,
  title,
  subtitle,
  timestamp,
  timestampLabel,
  unreadCount = 0,
  unreadBadgeTestID,
  selected = false,
  selectedBackgroundColor = '$mono200',
  onPress,
}: ConversationRowProps) => {
  const resolvedTimestampLabel =
    timestampLabel ?? formatConversationTimestamp(timestamp);

  return (
    <XStack
      alignItems={'center'}
      backgroundColor={selected ? selectedBackgroundColor : '$mono100'}
      gap={'$3'}
      onPress={onPress}
      paddingHorizontal={'$3'}
      paddingVertical={'$3'}
      pressStyle={pressStyle}
      {...(Platform.OS === 'web' ? { cursor: 'pointer' } : undefined)}
      {...getTestProps(testID)}
    >
      {avatar}
      <YStack flex={1} gap={'$1'} minWidth={0}>
        <XStack
          alignItems={'center'}
          justifyContent={'space-between'}
          gap={'$3'}
        >
          <LabelM color={'$mono900'} fontWeight={'$2'} numberOfLines={1}>
            {title}
          </LabelM>
          {resolvedTimestampLabel ? (
            <BodyS color={'$mono500'} flexShrink={0}>
              {resolvedTimestampLabel}
            </BodyS>
          ) : null}
        </XStack>
        <XStack
          alignItems={'center'}
          justifyContent={'space-between'}
          gap={'$3'}
        >
          <BodyS color={'$mono500'} flex={1} numberOfLines={1}>
            {subtitle}
          </BodyS>
          {unreadCount > 0 ? (
            <YStack
              alignItems={'center'}
              backgroundColor={'$brand500'}
              borderRadius={999}
              justifyContent={'center'}
              minWidth={'$5'}
              paddingHorizontal={'$1.5'}
              paddingVertical={'$0.5'}
              {...getTestProps(unreadBadgeTestID)}
            >
              <BodyS color={'$mono100'} fontWeight={'$2'}>
                {unreadCount}
              </BodyS>
            </YStack>
          ) : null}
        </XStack>
      </YStack>
    </XStack>
  );
};

export { ConversationRow };
